import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { take, switchMap, map, filter } from 'rxjs/operators';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Store, select } from '@ngrx/store';

import { getCustomerConfigState } from '../reducers';
import { NewsItems } from '../models/news';
import { Customer } from '../models/customer';
import { Menu } from '../models/menu';
import { MBanner } from '../models/banner';
import { ConfigService } from '@uxdf/direct-shared-lib';
import { menuListResponse, layout } from './utils/menu-list.mock';

@Injectable({
  providedIn: 'root'
})
export class BackendService {
  storeCode = 'default';
  url = {
    localeList: () => 'rest/all/V1/amadeus/public/locale/list',
    menu: customer => 'rest/' + customer.default_store_code + '/V1/amadeus/public/menu/list',
    guestMenu: store => 'rest/' + store + '/V1/amadeus/public/menu/list',
    modularityConfig: country_code => 'modularity/config?country=' + country_code,
    customer: store => 'rest/' + store + '/V1/amadeus/private/customer/config',
    contact: store => 'rest/' + store + '/V1/amadeus/private/contact_us/config',
    magento_login:  'amadeus/private/customer/login',
    social: store => 'rest/' + store + '/V1/amadeus/public/social_links/config',
    cartItems: store => 'rest/' + store + '/V1/amadeus/private/cart/items',
    wishlistItems: store => 'rest/' + store + '/V1/amadeus/private/wishlist/items'
  };
  private readonly jwt: JwtHelperService;

  constructor(
    @Inject('API') private readonly api: any,
    private readonly httpClient: HttpClient,
    private readonly store: Store<any>,
    private readonly initService: ConfigService
  ) {
    this.jwt = new JwtHelperService();
  }

  getPosts(store, filterObj): Observable<NewsItems> {
    return this.httpClient.get<NewsItems>(this.initService.getEnvConfig().AD_MAGENTO_URL + this.api.news.posts(store), {
      params: new HttpParams({ fromObject: filterObj })
    });
  }

  initCustomerConfig(): Observable<Customer> {
     return this.httpClient.get<Customer>(this.initService.getEnvConfig().AD_MAGENTO_URL + this.url.magento_login);
  }

  initMenuList(): Observable<Menu> {
    return of(menuListResponse);
  }

  initGuestMenuList(storeCode: string): Observable<Menu> {
    return this.httpClient.get<Menu>(this.initService.getEnvConfig().AD_MAGENTO_URL + this.url.guestMenu(storeCode));
  }

  getModularityConfig(): Observable<any> {
        const url = this.initService.getEnvConfig().AD_MAGENTO_URL + this.url.modularityConfig(localStorage.getItem('country_code'));
        return this.httpClient.get<any>(url, { headers: this.getJwtHeader() }).pipe(
          map(response => ({ ...response, layout }))
        );
  }

  getJwtHeader() {
    const token = localStorage.getItem('restoreToken');
    const headers = { Authorization: `Bearer ${token}` };
    return headers;
  }
  encryptedSSOParams() {
    return this.httpClient.get<any>(this.initService.getEnvConfig().AD_JAVA_API_URL + this.api.sso.ssoEncrypted());
  }

  healthCheck(): Observable<any> {
    return this.httpClient.get<any>(this.initService.getEnvConfig().AD_JAVA_API_URL);
  }

  getCartItems(storeCode, isDecisionMaker): Observable<any> {
    const urlPostfix = isDecisionMaker ? this.url.cartItems(storeCode) : this.url.wishlistItems(storeCode);
    return this.httpClient.get<any>(this.initService.getEnvConfig().AD_MAGENTO_URL + urlPostfix);
  }

  getBanner(storeCode: string, role: number, segment: number): Observable<MBanner[]> {
    return this.httpClient.get<MBanner[]>(this.initService.getEnvConfig().AD_MAGENTO_URL + this.api.banner(storeCode, role, segment));
  }

  /**
   * Add Magento scope for granular caching (provided by Magento)
   */
}
